import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import ViewKanbanRoundedIcon from '@mui/icons-material/ViewKanbanRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BreadcrumbsComponent from '../BreadcrumbsComponent';
import SearchTextField from '../SearchTextField';
import AddPeopleToProject from '../../containers/Dashboard/ProjectPages/AddPeopleToProject';
import RemoveUserFromProject from '../RemoveUserFromProject';
import ManageRolesModal from "../ManageRoles";
import DeleteTaskStatus from '../DeleteTaskStatus';
import BackgroundLetterAvatarsLogo from '../AvatarLogo';
import BoardStatusList from './BoardStatusList';
import { getActiveProjectData } from "../../common/Redux/Reducer";
import useProjectRoles from "../../common/useProjectRoles";
import useIsProjectCreator from "../../common/useIsProjectCreator";
import { useToast } from "../Toast";
import { NormalSkeleton } from '../../common/Skeleton';
import axios from 'axios';
import API_BASE_URL from '../config/appConfig';
import './projectaccesscontrol.scss';

/* every settings write is checked against the creator server-side, so each
   one has to say who is asking */
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

// employeeId is not always present (invited, not yet registered)
const getMemberId = (row) => row?.employeeId || row?.employeeEmail;

const ProjectAccessControl = () => {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectSlug } = useParams();
  const { Projects } = useSelector((state) => state.ProjamAppSlice);

  const projectData = Projects?.selectedProject?.selectedProjectData;
  // the store can still hold the previous project for a moment after navigating
  const projectLoaded = Boolean(projectData) && (!projectSlug || projectData?.slugString === projectSlug);
  const project = projectLoaded ? projectData : null;
  const projectId = project?._id;
  const Projectslug = project?.slugString || Projects?.selectedProject?.projectSlug;
  const { ready, isCreator, creatorId } = useIsProjectCreator(project);

  const [contributors, setContributors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
  const [openAddPeopleModel, setOpenAddPeopleModel] = useState(false);
  const [openManageRolesModel, setOpenManageRolesModel] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);

  /* roles belong to this project, so every list below reads the same source */
  const {
    roles: projectRoles,
    roleNames,
    loading: rolesLoading,
    error: rolesError,
    refresh: refreshRoles,
    createRole,
    renameRole,
    deleteRole,
  } = useProjectRoles(projectId);

  useEffect(() => {
    if (Projects?.selectedProject?.contributors) {
      setContributors(Projects.selectedProject.contributors);
    }
  }, [Projects?.selectedProject?.contributors]);

  /* one round trip, so the board and the member list pick the change up
     without a reload — the sprint board reads the same store slice */
  const refreshProject = async () => {
    if (Projectslug) await dispatch(getActiveProjectData(Projectslug));
  };

  /* Renaming a role moves the members holding it, so pull the project back
     down on the way out — otherwise the table keeps showing the old name. */
  const closeManageRoles = () => {
    setOpenManageRolesModel(false);
    refreshProject();
  };

  /* ----------------------------------------------------------------- members */
  const filteredMembers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return contributors.filter((member) => {
      const matchesTerm = !term
        || (member.employeeName || '').toLowerCase().includes(term)
        || (member.employeeEmail || '').toLowerCase().includes(term);
      const matchesRole = selectedRoles.length === 0 || selectedRoles.includes(member.role);
      return matchesTerm && matchesRole;
    });
  }, [contributors, searchTerm, selectedRoles]);

  const toggleRoleFilter = (role) => {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const isCreatorRow = (row) => Boolean(creatorId) && String(row?.employeeId || '') === creatorId;

  const handleRoleChange = async (member, newRole) => {
    const previous = contributors;
    setContributors((prev) =>
      prev.map((each) => (getMemberId(each) === getMemberId(member) ? { ...each, role: newRole } : each))
    );
    try {
      await axios.put(
        `${API_BASE_URL}/projects/updateMemberRole`,
        { memberEmail: member.employeeEmail, project: Projectslug, role: newRole },
        { headers: authHeaders() }
      );
      toast.success(`${member.employeeName || member.employeeEmail} is now ${newRole}`);
    } catch (error) {
      setContributors(previous);
      toast.error(errorMessage(error, "Could not change that role. Please try again."));
    }
  };

  const handleConfirmRemove = async () => {
    if (!userToRemove) return;
    try {
      await axios.post(
        `${API_BASE_URL}/projects/removeMember?projectid=${projectId}`,
        { employeeId: userToRemove.employeeId },
        { headers: authHeaders() }
      );
      setContributors((prev) => prev.filter((each) => getMemberId(each) !== getMemberId(userToRemove)));
      toast.success(`${userToRemove.employeeName || "Member"} removed from the project`);
    } catch (error) {
      toast.error(errorMessage(error, "Could not remove that member. Please try again."));
    } finally {
      setUserToRemove(null);
    }
  };

  const memberColumns = [
    {
      field: 'employeeName',
      headerName: 'Member',
      flex: 1.4,
      minWidth: 220,
      renderCell: ({ row }) => (
        <div className="member-cell">
          <BackgroundLetterAvatarsLogo
            LogoTitle={row.employeeName || row.employeeEmail}
            RequiredFontSize="11px !important"
          />
          <div className="member-cell__text">
            <span className="member-cell__name">
              {row.employeeName || 'Invited'}
              {isCreatorRow(row) ? <span className="member-badge">Creator</span> : null}
            </span>
            <span className="member-cell__email">{row.employeeEmail}</span>
          </div>
        </div>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => {
        const current = row.role || '';
        if (isCreatorRow(row)) {
          return <span className="member-role-static">{current || 'Project Owner'}</span>;
        }
        // a member can hold a role that is no longer on the project's list;
        // show it too, otherwise MUI renders an empty box and warns
        const options = !current || roleNames.includes(current) ? roleNames : [current, ...roleNames];
        return (
          <Select
            value={current}
            onChange={(e) => handleRoleChange(row, e.target.value)}
            size="small"
            className="role-select"
            inputProps={{ 'aria-label': `Role for ${row.employeeName || row.employeeEmail}` }}
            // the grid's cell handlers otherwise swallow the click
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            MenuProps={{ onClick: (event) => event.stopPropagation() }}
          >
            {options.map((role) => (
              <MenuItem key={role} value={role}>{role}</MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: 'action',
      headerName: '',
      width: 120,
      sortable: false,
      align: 'right',
      renderCell: ({ row }) => (isCreatorRow(row) ? null : (
        <Button
          className="remove-user-btn"
          onClick={() => setUserToRemove(row)}
          startIcon={<PersonRemoveRoundedIcon />}
        >
          Remove
        </Button>
      )),
    },
  ];

  /* ------------------------------------------------------------------- board */
  const statuses = useMemo(
    () => (project?.IssueStatus || [])
      .filter((status) => status?._id)
      .map((status) => ({ id: status._id, title: status.title })),
    [project?.IssueStatus]
  );

  /* Each handler rethrows after reporting, so the list can roll its own state
     back (or keep its editor open) when the server says no. */
  const withBoardWrite = async (request, success, failure) => {
    setStatusBusy(true);
    try {
      await request();
      await refreshProject();
      if (success) toast.success(success);
    } catch (error) {
      toast.error(errorMessage(error, failure));
      throw error;
    } finally {
      setStatusBusy(false);
    }
  };

  const handleReorder = (order) =>
    withBoardWrite(
      () => axios.put(`${API_BASE_URL}/projects/reorderIssueStatus`, { projectId, order }, { headers: authHeaders() }),
      'Board order saved',
      'Could not save the new order. Please try again.'
    );

  const handleRename = (status, title) =>
    withBoardWrite(
      () => axios.put(`${API_BASE_URL}/projects/UpdateIssueStatus/${status.id}`, { projectId, status: title }, { headers: authHeaders() }),
      `Renamed to "${title}"`,
      'Could not rename that column. Please try again.'
    );

  const handleAdd = (title) =>
    withBoardWrite(
      () => axios.post(`${API_BASE_URL}/projects/addIssueStatus`, { projectId, status: title }, { headers: authHeaders() }),
      `"${title}" added to the board`,
      'Could not add that column. Please try again.'
    );

  const handleDeleteStatusConfirm = async () => {
    const target = statusToDelete;
    setStatusToDelete(null);
    if (!target) return;
    try {
      await withBoardWrite(
        () => axios.delete(`${API_BASE_URL}/projects/removeIssueStatus/${target.id}`, { data: { projectId }, headers: authHeaders() }),
        `"${target.title}" removed from the board`,
        'Could not delete that column. Please try again.'
      );
    } catch {
      // already reported
    }
  };

  /* ------------------------------------------------------------------ render */
  const creatorName = contributors.find(isCreatorRow)?.employeeName;

  if (!projectLoaded || !ready) {
    return (
      <Box className="project-settings-page" aria-busy="true">
        <div className="ps-hero"><NormalSkeleton width={240} height={32} /></div>
        <div className="ps-layout">
          <div className="ps-card"><NormalSkeleton variant="rectangular" width="100%" height={320} /></div>
          <div className="ps-card"><NormalSkeleton variant="rectangular" width="100%" height={320} /></div>
        </div>
      </Box>
    );
  }

  if (!isCreator) {
    return (
      <Box className="project-settings-page">
        <div className="ps-card ps-locked" role="alert">
          <span className="ps-locked__icon"><LockRoundedIcon /></span>
          <Typography component="h1" className="ps-locked__title">Project settings are locked</Typography>
          <Typography className="ps-locked__text">
            Only {creatorName ? <strong>{creatorName}</strong> : "the person who created this project"} can
            manage its members, roles and board.
          </Typography>
          <Button
            className="ps-btn is-primary"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(`/projects/${Projectslug}/active-sprints`)}
          >
            Back to the board
          </Button>
        </div>
      </Box>
    );
  }

  return (
    <Box className="project-settings-page">
      <header className="ps-hero">
        <div className="ps-hero__crumbs"><BreadcrumbsComponent /></div>
        <div className="ps-hero__main">
          <span className="ps-hero__tile" aria-hidden="true"><TuneRoundedIcon /></span>
          <div>
            <Typography component="h1" className="ps-hero__title">Project settings</Typography>
            <div className="ps-hero__meta">
              <span className="ps-chip is-key">{project?.key || 'Project'}</span>
              <span className="ps-chip"><GroupRoundedIcon />{contributors.length} {contributors.length === 1 ? 'member' : 'members'}</span>
              <span className="ps-chip"><ViewKanbanRoundedIcon />{statuses.length} board {statuses.length === 1 ? 'column' : 'columns'}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="ps-layout">
        <section className="ps-card" aria-labelledby="ps-members-title">
          <div className="ps-card__head">
            <div>
              <h2 id="ps-members-title" className="ps-card__title"><GroupRoundedIcon />Members</h2>
              <p className="ps-card__desc">People who can view and work on this project, and what they can do.</p>
            </div>
            <div className="ps-card__actions">
              <Button className="ps-btn is-ghost" startIcon={<AdminPanelSettingsRoundedIcon />} onClick={() => setOpenManageRolesModel(true)}>
                Manage roles
              </Button>
              <Button className="ps-btn is-primary" startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => setOpenAddPeopleModel(true)}>
                Add people
              </Button>
            </div>
          </div>

          <div className="ps-filterbar">
            <SearchTextField searchTerm={searchTerm} handleSearch={setSearchTerm} />
            <Button
              className={`ps-filter-btn ${selectedRoles.length ? 'is-active' : ''}`}
              startIcon={<FilterListRoundedIcon />}
              onClick={(e) => setRoleMenuAnchor(e.currentTarget)}
            >
              {selectedRoles.length ? `Roles · ${selectedRoles.length}` : 'All roles'}
            </Button>
            {selectedRoles.length ? (
              <button type="button" className="ps-link" onClick={() => setSelectedRoles([])}>Clear</button>
            ) : null}
            <Menu anchorEl={roleMenuAnchor} open={Boolean(roleMenuAnchor)} onClose={() => setRoleMenuAnchor(null)}>
              {roleNames.map((role) => (
                <MenuItem key={role} dense onClick={() => toggleRoleFilter(role)}>
                  <Checkbox size="small" checked={selectedRoles.includes(role)} />
                  <ListItemText primary={role} />
                </MenuItem>
              ))}
            </Menu>
          </div>

          <div className="ps-grid">
            <DataGrid
              rows={filteredMembers}
              columns={memberColumns}
              getRowId={getMemberId}
              autoHeight
              rowHeight={60}
              disableRowSelectionOnClick
              disableColumnMenu
              // a page is at most 50 rows, so render them all
              disableVirtualization
              initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
              pageSizeOptions={[10, 25, 50]}
              localeText={{ noRowsLabel: searchTerm || selectedRoles.length ? 'No members match these filters' : 'No members yet' }}
            />
          </div>
        </section>

        <section className="ps-card" aria-labelledby="ps-board-title">
          <div className="ps-card__head">
            <div>
              <h2 id="ps-board-title" className="ps-card__title"><ViewKanbanRoundedIcon />Board columns</h2>
              <p className="ps-card__desc">
                Drag <Tooltip title="Use the handle, or focus it and press Space then the arrow keys"><span className="ps-hint">⋮⋮</span></Tooltip> to
                set the order columns appear on the sprint board. Double-click a name to rename it.
              </p>
            </div>
          </div>
          <BoardStatusList
            statuses={statuses}
            busy={statusBusy}
            onReorder={handleReorder}
            onRename={handleRename}
            onAdd={handleAdd}
            onDelete={setStatusToDelete}
          />
        </section>
      </div>

      {openAddPeopleModel && (
        <AddPeopleToProject open={openAddPeopleModel} setOpenAddPeopleModel={setOpenAddPeopleModel} />
      )}

      {openManageRolesModel && (
        <ManageRolesModal
          open={openManageRolesModel}
          handleClose={closeManageRoles}
          projectName={project?.title || "this project"}
          roles={projectRoles}
          loading={rolesLoading}
          loadError={rolesError}
          refreshRoles={refreshRoles}
          contributors={contributors}
          createRole={createRole}
          renameRole={renameRole}
          deleteRole={deleteRole}
        />
      )}

      {statusToDelete && (
        <DeleteTaskStatus
          open={Boolean(statusToDelete)}
          handleClose={() => setStatusToDelete(null)}
          handleDelete={handleDeleteStatusConfirm}
        />
      )}

      {userToRemove && (
        <RemoveUserFromProject
          open={Boolean(userToRemove)}
          handleClose={() => setUserToRemove(null)}
          userToRemove={userToRemove}
          handleConfirmRemove={handleConfirmRemove}
        />
      )}
    </Box>
  );
};

export default ProjectAccessControl;
